import type { FilingDeadlineDto } from '@signalvote/shared';

/**
 * The two treasurer-facing notes a deadline can carry onto any surface that renders its due date:
 * the applicability question (surfaced instead of a hard suppression — Mississippi's pre-election
 * unopposed exemption) and the receipt exposure (a postmark does not count; the $50/day-capped-$500
 * penalty). Both render only when present, so a jurisdiction that declares neither is unchanged.
 * One component so a copy or layout change is a single edit across the dashboard card, the close
 * masthead, and the upcoming-filings strip.
 *
 * `as` picks the wrapper element: 'p' for block contexts, 'span' for a context that forbids a <p>
 * (the strip row is an <a>). `testIdPrefix` yields `<prefix>-applicability-question` /
 * `<prefix>-filing-exposure`.
 */
export function FilingExposureNotes({
  deadline,
  testIdPrefix,
  as = 'p',
  size = 'sm',
}: {
  deadline: Pick<FilingDeadlineDto, 'applicabilityQuestion' | 'filingExposureNote'>;
  testIdPrefix: string;
  as?: 'p' | 'span';
  size?: 'sm' | 'xs';
}) {
  const Tag = as;
  const block = as === 'span' ? 'block ' : '';
  const text = size === 'xs' ? 'text-[10.5px]' : 'text-[12.5px]';

  return (
    <>
      {deadline.applicabilityQuestion && (
        <Tag
          data-testid={`${testIdPrefix}-applicability-question`}
          className={`${block}${text} leading-snug text-slate-600 mt-1`}
        >
          {deadline.applicabilityQuestion}
        </Tag>
      )}
      {deadline.filingExposureNote && (
        <Tag
          data-testid={`${testIdPrefix}-filing-exposure`}
          className={`${block}${text} leading-snug text-slate-500 mt-1`}
        >
          {deadline.filingExposureNote}
        </Tag>
      )}
    </>
  );
}
